/**
 * DatePicker — a pill trigger that opens a portaled month-grid calendar.
 *
 * Ported 1:1 from `tw-components.jsx:336-421`. Deviations are mechanical only:
 * `cx` and `Icon` are imported, `React` is imported for hooks, and it portals
 * through `react-dom`'s `createPortal` the same way the vendored `Dialog` does.
 *
 * Fully controlled: `value`/`onChange` take and give an `"YYYY-MM-DD"` string.
 * Positions itself on open by measuring the trigger's `getBoundingClientRect()`
 * and flips to drop upward when there isn't room below — recomputed each time
 * it opens, not on scroll/resize while open (it just closes instead, same as
 * upstream).
 *
 * Needs `dnd-fade` and `dnd-select-in` from ./keyframes (both already vendored
 * for other components).
 *
 * One addition (Oct 2026): `isDateDisabled(iso) => boolean` greys out single
 * days inside `min`/`max` — the return-pickup picker blocks Sundays with it.
 * Upstream only understands `min`/`max`.
 *
 * One trim from upstream: `footNote` is passed through as a plain string instead
 * of upstream's `{day}`-templated string + `dpOrdinal()` ("the 24th"). No consumer
 * here needs the ordinal-day templating; add `dpOrdinal` back from
 * `tw-components.jsx:343` if one does.
 */
import React from "react";
import { createPortal } from "react-dom";

import { cx } from "./cx";
import { Icon } from "./Icon.jsx";
import "./keyframes";

const DP_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const DP_MON_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const DP_DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const DP_DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const dpParse = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || "");
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
};
const dpISO = (d) =>
  d.getFullYear() +
  "-" +
  String(d.getMonth() + 1).padStart(2, "0") +
  "-" +
  String(d.getDate()).padStart(2, "0");
const dpLabel = (d, noYear) =>
  DP_DAY_SHORT[d.getDay()] +
  ", " +
  DP_MON_SHORT[d.getMonth()] +
  " " +
  d.getDate() +
  (noYear ? "" : ", " + d.getFullYear());

// Shared calendar primitives — kept factored out of DatePicker so any future
// month grid composes from these instead of being hand-rolled beside it.
const CAL_NAV =
  "inline-flex items-center justify-center w-[32px] h-[32px] rounded-full border-0 bg-transparent text-ink-2 cursor-pointer [transition:background_var(--duration-fast),color_var(--duration-fast),transform_var(--duration-base)_var(--ease-spring)] hover:bg-neutral-100 hover:text-ink active:scale-[0.9] disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:bg-transparent";
const CAL_DAY_BASE =
  "relative h-[38px] inline-flex items-center justify-center rounded-full border-0 font-sans text-sm cursor-pointer tabular-nums [transition:background_var(--duration-fast),color_var(--duration-fast),box-shadow_var(--duration-base)_var(--ease-out),transform_var(--duration-base)_var(--ease-spring)] focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]";
const CAL_DAY_REST =
  "bg-transparent text-ink-2 hover:bg-neutral-100 hover:text-ink active:scale-[0.9]";
const CAL_DAY_ON =
  "bg-orange-500 text-white font-bold shadow-brand active:scale-[0.9]";
const CAL_DAY_OFF =
  "bg-transparent text-[var(--text-disabled)] cursor-not-allowed";
const CAL_DOT =
  "absolute bottom-[5px] left-1/2 -translate-x-1/2 w-[4px] h-[4px] rounded-full";

function CalendarNav({ label, onPrev, onNext, prevDisabled, nextDisabled }) {
  return (
    <div className="flex items-center justify-between mb-[var(--space-2)]">
      <button
        type="button"
        className={CAL_NAV}
        disabled={prevDisabled}
        onClick={onPrev}
        aria-label="Previous month"
      >
        <Icon name="chevron_left" size={20} weight={500} />
      </button>
      <span
        key={label}
        className="text-sm font-bold text-ink animate-[dnd-fade_var(--duration-base)_var(--ease-out)]"
      >
        {label}
      </span>
      <button
        type="button"
        className={CAL_NAV}
        disabled={nextDisabled}
        onClick={onNext}
        aria-label="Next month"
      >
        <Icon name="chevron_right" size={20} weight={500} />
      </button>
    </div>
  );
}

function CalendarWeekdays() {
  return (
    <div className="grid grid-cols-7 gap-[2px] mb-[2px]">
      {DP_DOW.map((d, i) => (
        <span
          key={i}
          className="h-[24px] inline-flex items-center justify-center text-2xs font-bold uppercase tracking-[var(--tracking-wide)] text-ink-3"
        >
          {d}
        </span>
      ))}
    </div>
  );
}

export function DatePicker({
  label,
  value,
  onChange,
  min,
  max,
  // Local addition (not in the upstream kit): `(iso) => boolean` greys out
  // individual days inside min/max — e.g. Sundays for Purolator pickups.
  isDateDisabled,
  today: todayISO,
  placeholder = "Pick a date",
  helperText,
  disabled = false,
  footNote,
  hideYear = false,
  style = {},
  className = "",
}) {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState(null);
  const ref = React.useRef(null);
  const popRef = React.useRef(null);
  const sel = dpParse(value);
  const minD = dpParse(min);
  const maxD = dpParse(max);
  const today =
    dpParse(todayISO) ||
    (() => {
      const t = new Date();
      t.setHours(0, 0, 0, 0);
      return t;
    })();
  const [view, setView] = React.useState(() => {
    const b = sel || minD || today;
    return { y: b.getFullYear(), m: b.getMonth() };
  });
  React.useEffect(() => {
    if (open) {
      const b = sel || minD || today;
      setView({ y: b.getFullYear(), m: b.getMonth() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (
        ref.current &&
        !ref.current.contains(e.target) &&
        !(popRef.current && popRef.current.contains(e.target))
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onScroll = (e) => {
      if (popRef.current && popRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  const first = new Date(view.y, view.m, 1);
  const daysIn = new Date(view.y, view.m + 1, 0).getDate();
  const lead = first.getDay();
  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysIn; d++) cells.push(new Date(view.y, view.m, d));
  while (cells.length % 7 !== 0) cells.push(null);
  const blocked = (d) =>
    (minD && d < minD) ||
    (maxD && d > maxD) ||
    Boolean(isDateDisabled && isDateDisabled(dpISO(d)));
  const monthBlocked = (dir) => {
    const edge =
      dir < 0 ? new Date(view.y, view.m, 0) : new Date(view.y, view.m + 1, 1);
    return dir < 0 ? minD && edge < minD : maxD && edge > maxD;
  };
  const step = (dir) =>
    setView((v) => {
      const n = new Date(v.y, v.m + dir, 1);
      return { y: n.getFullYear(), m: n.getMonth() };
    });
  const pick = (d) => {
    if (onChange) onChange(dpISO(d));
    setOpen(false);
  };

  return (
    <div
      className={cx(
        "dnd-datepicker relative flex flex-col gap-[var(--space-2)] font-sans",
        className
      )}
      style={style}
      ref={ref}
    >
      {label && <span className="text-sm font-semibold text-ink">{label}</span>}
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={(e) => {
          if (!open) {
            const r = e.currentTarget.getBoundingClientRect();
            const estH = 356;
            const dropUp =
              r.bottom + estH > window.innerHeight - 8 &&
              r.top > window.innerHeight - r.bottom;
            setPos({
              left: Math.min(r.left, Math.max(8, window.innerWidth - 316)),
              ...(dropUp
                ? { bottom: window.innerHeight - r.top + 6 }
                : { top: r.bottom + 6 }),
              dropUp,
            });
          }
          setOpen((o) => !o);
        }}
        className={cx(
          "dnd-datepicker__trigger flex items-center gap-[var(--space-2)] w-full h-[var(--control-height-md)] pl-[var(--space-5)] pr-[var(--space-4)] bg-card border-[1.5px] rounded-[var(--radius-pill)] cursor-pointer font-sans text-base text-ink text-left",
          "[transition:border-color_var(--duration-fast),box-shadow_var(--duration-base)_var(--ease-out)] focus-visible:outline-none focus-visible:border-line-strong focus-visible:shadow-md",
          open
            ? "border-line-strong shadow-md"
            : "border-line hover:border-line-strong",
          disabled && "bg-neutral-100 cursor-not-allowed"
        )}
      >
        <Icon
          className={cx(open ? "text-ink-2" : "text-ink-3")}
          name="calendar_month"
          size={20}
          weight={400}
        />
        <span
          className={cx(
            "flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap",
            !sel && "text-ink-3"
          )}
        >
          {sel ? dpLabel(sel, hideYear) : placeholder}
        </span>
        <Icon
          className={cx(
            "text-ink-3 [transition:transform_var(--duration-base)_var(--ease-spring)]",
            open && "rotate-180"
          )}
          name="expand_more"
          size={20}
          weight={500}
        />
      </button>
      {helperText && <span className="text-xs text-ink-2">{helperText}</span>}
      {open &&
        pos &&
        createPortal(
          <div
            ref={popRef}
            role="dialog"
            aria-label="Choose a date"
            style={{
              position: "fixed",
              left: pos.left,
              top: pos.top,
              bottom: pos.bottom,
              width: 308,
            }}
            className={cx(
              "dnd-datepicker__pop z-[var(--z-tooltip)] bg-card border border-line-subtle rounded-md shadow-lg p-[var(--space-3)] font-sans animate-[dnd-select-in_var(--duration-base)_var(--ease-out)]",
              pos.dropUp ? "origin-bottom" : "origin-top"
            )}
          >
            <CalendarNav
              label={DP_MONTHS[view.m] + " " + view.y}
              onPrev={() => step(-1)}
              onNext={() => step(1)}
              prevDisabled={monthBlocked(-1)}
              nextDisabled={monthBlocked(1)}
            />
            <CalendarWeekdays />
            <div className="grid grid-cols-7 gap-[2px]">
              {cells.map((d, i) => {
                if (!d) return <span key={"e" + i} className="h-[38px]" />;
                const iso = dpISO(d);
                const isSel = sel && dpISO(sel) === iso;
                const isToday = dpISO(today) === iso;
                const off = blocked(d);
                return (
                  <button
                    key={iso}
                    type="button"
                    disabled={off}
                    onClick={() => pick(d)}
                    aria-pressed={isSel || undefined}
                    className={cx(
                      CAL_DAY_BASE,
                      off ? CAL_DAY_OFF : isSel ? CAL_DAY_ON : CAL_DAY_REST,
                      !off && !isSel && isToday && "font-bold text-blue-700"
                    )}
                  >
                    {d.getDate()}
                    {isToday && !isSel && (
                      <span className={cx(CAL_DOT, "bg-blue-600")} />
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-between gap-[var(--space-2)] mt-[var(--space-2)] pt-[var(--space-2)] border-t border-line-subtle">
              <span className="text-xs text-ink-2 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                {sel ? footNote || dpLabel(sel) : "No date picked yet"}
              </span>
              <button
                type="button"
                disabled={blocked(today)}
                onClick={() => pick(today)}
                className="shrink-0 border-0 bg-transparent font-sans text-xs font-semibold text-blue-600 cursor-pointer rounded-sm px-[var(--space-2)] py-[4px] [transition:background_var(--duration-fast),color_var(--duration-fast)] hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              >
                Today
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
